import React, { useCallback, useEffect, useState } from 'react';
import { DNABox, Iffy } from '@alucio/lux-ui';
import DNAEmpty, { EmptyVariant } from 'src/components/DNA/Empty/DNAEmpty';
import SelectFileButton from 'src/components/SelectFileButton/SelectFileButton';
import DocumentDropzone from '../Documents/DocumentDropzone/DocumentDropzone';
import { useAllPersonalDocuments } from 'src/state/redux/selector/document';
import { useDNADocumentActions } from 'src/components/DNA/Document/DNADocument.actions';
import { DocumentORM, FolderItemORM, FolderORM, isDocumentORM } from 'src/types/types';
import { useHistory } from 'src/router';
import ROUTES from 'src/router/routeDef';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { DNAFolderGridList, TitleBar } from 'src/components/DNA';
import { FolderVariants } from './constants';
import { useAppSettings } from 'src/state/context/AppSettings';

// Reusable upload button component to avoid DRY
const UploadButton: React.FC<{
  onSelect: (files: File[]) => void;
  disableButtonGroup?: boolean;
  appearance?: 'outline' | 'filled' | 'ghost';
}> = ({
  onSelect,
  disableButtonGroup,
  appearance = 'filled',
}) => (
  <SelectFileButton
    acceptedTypes=".pptx,.pdf"
    testID="upload-button"
    onSelect={onSelect}
    text="Upload"
    appearance={appearance}
    style={ { borderRadius: 6 } }
    status="primary"
    disableButtonGroup={disableButtonGroup}
  />
);

const MyUploads = () => {
  const [filesToUpload, setFilesToUpload] = useState<File[]>([])
  const { deviceMode } = useAppSettings();
  const isTablet = deviceMode === 'tablet';

  const history = useHistory();
  const enableMyUploads = useFeatureFlags('enableMyUploads');

  useEffect(() => {
    /*
     Redirect to 404 if my uploads is not enabled
     Ideally this would be placed in routes.tsx, however with the way the components were structured this is the best way
     to do it without major rewrites
    */
    if (!enableMyUploads) {
      history.replace(ROUTES.NOT_FOUND.PATH);
    }
  }, []);

  function onSelectedFiles(files: File[]): void {
    setFilesToUpload(files);
  }

  const personalDocuments = useAllPersonalDocuments();
  const documentActions = useDNADocumentActions();

  const onPress = useCallback((selectedItemORM: FolderItemORM | FolderORM | DocumentORM) => {
    if (isDocumentORM(selectedItemORM)) {
      const { latestUsableDocumentVersionORM } = selectedItemORM.relations.version
      if (latestUsableDocumentVersionORM.model.conversionStatus === 'PROCESSED') {
        documentActions.preview(selectedItemORM)();
      }
    }
  }, [])

  return (
    <DNABox appearance="col" fill>
      <TitleBar
        title="My Uploads"
        description="Ensure your files have been approved for external sharing before uploading"
      >
        <TitleBar.Button>
          <Iffy is={!isTablet}>
            <UploadButton
              onSelect={onSelectedFiles}
              appearance="outline"
              disableButtonGroup
            />
          </Iffy>
        </TitleBar.Button>
      </TitleBar>

      <DocumentDropzone
        files={filesToUpload}
        onCloseModal={() => setFilesToUpload([])}
        toggleMyUnpublishedDocument={() => undefined}
        isUserDocument
        disableDrag
      >
        <Iffy is={!personalDocuments.length}>
          <DNAEmpty
            emptyVariant={EmptyVariant.CustomLabelEmpty}
            customEmptyLabel="Upload content to use in your presentations."
            customEmptyButton={<UploadButton onSelect={onSelectedFiles} appearance="filled" />}
          />
        </Iffy>
        <Iffy is={personalDocuments.length}>
          <DNAFolderGridList
            variant={FolderVariants.MY_UPLOADS}
            items={personalDocuments}
            emptyVariant={EmptyVariant.UploadsEmpty}
            onPress={onPress}
          />
        </Iffy>
      </DocumentDropzone>
    </DNABox>
  )
}

export default MyUploads;
